import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';
import { getEarnings } from '../services/apiService';
import apiService from '../services/apiService';
import { COMMON_TEXT_KEYS } from '../hooks/useMultilingualText';
import FullPageLoader from './FullPageLoader';

const Earnings = () => {
    const { isAuthenticated, currentUser, showSignIn, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    
    // Redirect to sign in if not authenticated (after auth initialization completes)
    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            showSignIn();
        }
    }, [authLoading, isAuthenticated, showSignIn]);
    
    // Handle window resize for responsive design
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // Temporary simplified version for debugging
    const getText = (key, fallback) => fallback || key;
    const [kycData, setKycData] = useState(null);
    const [earnings, setEarnings] = useState({
        totalEarnings: 0,
        availableBalance: 0,
        pendingAmount: 0,
        totalWithdrawn: 0
    });
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    // KYC modal state
    const [isKycModalOpen, setIsKycModalOpen] = useState(false);
    const [isKycPromptOpen, setIsKycPromptOpen] = useState(false);
    const [aadhaarUploaded, setAadhaarUploaded] = useState(false);
    const [panUploaded, setPanUploaded] = useState(false);
    const [aadhaarFile, setAadhaarFile] = useState(null);
    const [aadhaarPreviewUrl, setAadhaarPreviewUrl] = useState('');
    const [panFile, setPanFile] = useState(null);
    const [panPreviewUrl, setPanPreviewUrl] = useState('');
    const aadhaarInputRef = useRef(null);
    const panInputRef = useRef(null);
    // Claim modal state
    const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
    const [claimAmount, setClaimAmount] = useState('');
    const [upiId, setUpiId] = useState('');
    const [confirmUpiId, setConfirmUpiId] = useState('');
    const [claimSubmitting, setClaimSubmitting] = useState(false);
    // KYC verification state removed (unused)
    // Local/session persistence keys
    const KYC_STATUS_KEY = 'kycStatus';
    const KYC_PROMPT_DISMISSED_KEY = 'kycPromptDismissed';
    // Track whether KYC API has executed at least once
    const [kycLoaded, setKycLoaded] = useState(false);
    
    // Remove aggressive authentication check that might be causing issues
    // useEffect(() => {
    //     if (!isAuthenticated) {
    //         showSignIn();
    //     }
    // }, [isAuthenticated, showSignIn]);
    
    // Fetch earnings data from API
    const fetchEarningsData = useCallback(async () => {
        console.log('[Earnings] fetchEarningsData called, user:', currentUser);
        console.log('[Earnings] isAuthenticated:', isAuthenticated);
        
        if (!isAuthenticated || !currentUser?.mobileNumber) {
            console.log('[Earnings] Not authenticated or no mobile number, skipping API call');
            setLoading(false);
            return;
        }
        
        console.log('[Earnings] User mobile number found:', currentUser.mobileNumber, 'calling getEarnings API');
        
        try {
            setError(null);
            
            console.log('[Earnings] 🔄 Calling getEarnings API (KYC data handled separately)');
            const earningsResponse = await getEarnings();
            
            console.log('[Earnings] ✅ Earnings API response:', earningsResponse);
            
            const response = earningsResponse;
            
            if (response && response.success && response.data) {
                const data = response.data;
                console.log('[Earnings] Processing earnings data:', data);

                // New backend contract mapping
                const summary = data.earningSummary || {};
                const account = data.account_summary || {};
                const deposits = Array.isArray(account.deposit) ? account.deposit : [];
                const claims = Array.isArray(account.claim) ? account.claim : [];

                // Extract earnings summary for cards
                const earningsData = {
                    totalEarnings: Number(summary.total_earned_amount) || 0,
                    availableBalance: Number(summary.balance_claim_amount) || 0,
                    pendingAmount: Number(summary.claim_amount_underprocess) || 0,
                    totalWithdrawn: Number(summary.total_claimed_amount) || 0,
                };

                // Map deposits (positive earnings) and filter out zero amounts
                const depositTx = deposits
                    .map((d, idx) => {
                        const depositedOn = d.deposited_on;
                        const isCredited = depositedOn && depositedOn !== '0000-00-00 00:00:00';
                        const status = isCredited ? 'Credited' : 'Pending';
                        const amount = Number(d.earned_amount) || 0;
                        return {
                            id: d.id || `deposit-${idx}`,
                            date: formatDate(d.referred_on || depositedOn),
                            type: 'Referral Bonus',
                            typeIcon: getTransactionIcon('referral bonus'),
                            description: d.amount_earned_against || 'Referral',
                            details: isCredited ? '1 Month completion bonus' : 'Awaiting credit',
                            amount,
                            status,
                            statusIcon: getStatusIcon(status),
                            isEarning: true,
                        };
                    })
                    .filter(transaction => transaction.amount > 0); // Filter out zero amounts

                // Map claims (withdrawals) and filter out zero amounts
                const claimTx = claims
                    .map((c, idx) => {
                        const status = (c.claim_status || '').toUpperCase() === 'SUCCESS' ? 'Completed' : (c.claim_status || 'Pending');
                        const amount = -(Number(c.claimed_amount) || 0);
                        return {
                            id: c.utr_number || `claim-${idx}`,
                            date: formatDate(c.claimed_on || c.deposited_on),
                            type: 'UPI Withdrawal',
                            typeIcon: getTransactionIcon('upi withdrawal'),
                            description: c.upi_id || 'UPI Transfer',
                            details: c.bank_account_no ? `Account: ${c.bank_account_no}` : (c.utr_number ? `UTR: ${c.utr_number}` : 'Withdrawal'),
                            amount,
                            status,
                            statusIcon: getStatusIcon(status),
                            isEarning: false,
                        };
                    })
                    .filter(transaction => transaction.amount < 0); // Filter out zero amounts (keep negative amounts for withdrawals)

                // Combine and sort by date desc (fallback safe)
                const combined = [...depositTx, ...claimTx].sort((a, b) => {
                    const da = new Date(a.date);
                    const db = new Date(b.date);
                    return db - da;
                });

                setEarnings(earningsData);
                setTransactions(combined);
                console.log('[Earnings] Set earnings:', earningsData);
                console.log('[Earnings] Set transactions:', combined);

            } else {
                console.log('[Earnings] Unexpected response format, using fallback data');
                // Fallback to demo data if API response is unexpected
                setEarnings({
                    totalEarnings: 1750,
                    availableBalance: 750,
                    pendingAmount: 0,
                    totalWithdrawn: 1000
                });
                setTransactions([
                    { id: '1', date: 'Dec 25, 2024', type: 'Referral Bonus', typeIcon: 'fa-user-check', description: 'Rahul Kumar', details: '1 Month completion bonus', amount: 500, status: 'Credited', statusIcon: 'fa-check-circle', isEarning: true },
                    { id: '2', date: 'Dec 1, 2024', type: 'Referral Bonus', typeIcon: 'fa-user-plus', description: 'Priya Sharma', details: 'Joining bonus for TechCorp India', amount: 250, status: 'Credited', statusIcon: 'fa-check-circle', isEarning: true },
                    { id: '3', date: 'Nov 28, 2024', type: 'UPI Withdrawal', typeIcon: 'fa-mobile-alt', description: 'john@paytm', details: 'UPI transfer', amount: -1000, status: 'Completed', statusIcon: 'fa-check-circle', isEarning: false }
                ]);
            }
            
        } catch (err) {
            console.error('[Earnings] Error fetching earnings:', err);
            setError('Failed to load earnings data. Please try again.');
            // Set fallback data on error
            setEarnings({
                totalEarnings: 0,
                availableBalance: 0,
                pendingAmount: 0,
                totalWithdrawn: 0
            });
            setTransactions([]);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [isAuthenticated, currentUser]);
    
    // Helper functions
    const formatDate = (dateString) => {
        if (!dateString) return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
            return dateString;
        }
    };
    
    const getTransactionIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'withdrawal':
            case 'upi withdrawal':
                return 'fa-mobile-alt';
            case 'referral bonus':
            case 'completion bonus':
                return 'fa-user-check';
            case 'joining bonus':
                return 'fa-user-plus';
            default:
                return 'fa-coins';
        }
    };
    
    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'credited':
                return 'fa-check-circle';
            case 'pending':
                return 'fa-clock';
            case 'failed':
                return 'fa-times-circle';
            default:
                return 'fa-check-circle';
        }
    };
    
    // Refresh earnings data
    const refreshEarnings = async () => {
        setIsRefreshing(true);
        await fetchEarningsData();
    };
    
    // Load earnings data on component mount and when user changes
    useEffect(() => {
        console.log('[Earnings] Component mounted, calling fetchEarningsData');
        fetchEarningsData();
    }, [fetchEarningsData]);

    // Removed saved KYC helper to avoid stale gating and eslint warning

    // Immediate KYC details fetch - executes immediately when user is authenticated
    const fetchKycDetails = useCallback(async () => {
        if (!isAuthenticated || !currentUser?.mobileNumber) {
            console.log('[Earnings] Not authenticated or no mobile number, skipping KYC API call');
            return null;
        }

        try {
            console.log('[Earnings] 🔄 Fetching KYC details immediately...');
            const resp = await apiService.fetchCandidateKYCDetails();
            console.log('[Earnings] KYC fetch response:', resp);

            if (resp && resp.success) {
                const payload = resp.data || null;
                setKycData(payload);
                const aadhaarOk = !!(payload?.aadhaarVerified);
                const panOk = !!(payload?.panVerified);

                // Persist KYC status for subsequent checks
                try {
                    localStorage.setItem(
                        KYC_STATUS_KEY,
                        JSON.stringify({ aadhaarVerified: aadhaarOk, panVerified: panOk, ts: Date.now() })
                    );
                } catch (_) {}
                
                console.log('[Earnings] ✅ KYC data loaded:', { aadhaarVerified: aadhaarOk, panVerified: panOk });
                setKycLoaded(true);
                return payload;
            } else {
                console.warn('[Earnings] KYC fetch failed; will show KYC required');
                setKycLoaded(true);
                return null;
            }
        } catch (e) {
            console.error('[Earnings] Error during KYC check:', e);
            setKycLoaded(true);
            return null;
        }
    }, [isAuthenticated, currentUser]);

    // Execute KYC fetch immediately when component mounts and user is authenticated
    useEffect(() => {
        if (!authLoading && isAuthenticated && currentUser?.mobileNumber) {
            console.log('[Earnings] Component mounted with authenticated user, fetching KYC details immediately');
            fetchKycDetails();
        }
    }, [authLoading, isAuthenticated, currentUser, fetchKycDetails]);

    // While auth is initializing, show a neutral full-page loader
    if (authLoading) {
        return <FullPageLoader text="Loading..." />;
    }

    const handleClaimClick = async () => {
        // Always ensure we have a fresh API-backed KYC state before deciding
        let payload = kycData;
        if (!kycLoaded || !payload) {
            console.log('[Earnings] KYC data not loaded, fetching fresh data...');
            const latest = await fetchKycDetails();
            if (latest) {
                payload = latest;
            } else {
                payload = kycData;
            }
        } else {
            // ensure we use most recent state before proceeding
            payload = {
                ...payload,
                aadhaarVerified: !!payload?.aadhaarVerified,
                panVerified: !!payload?.panVerified
            };
        }

        // Decide strictly from API response data (do not use cached value for gating)
        const aadhaarOk = !!(payload?.aadhaarVerified);
        const panOk = !!(payload?.panVerified);
        const kycComplete = aadhaarOk && panOk;

        console.log('[Earnings] KYC status check:', { aadhaarOk, panOk, kycComplete });

        if (!kycComplete) {
            // If user previously dismissed the prompt, guide them to Profile > KYC instead of doing nothing
            const dismissed = sessionStorage.getItem(KYC_PROMPT_DISMISSED_KEY) === '1';
            if (dismissed) {
                navigate('/profile', { state: { scrollTo: 'kyc' } });
            } else {
                setIsKycPromptOpen(true);
            }
            return;
        }
        setIsClaimModalOpen(true);
        document.body.style.overflow = 'hidden';
    };

    // Removed inline KYC flow from Earnings; KYC is handled on Profile page
    const closeKycModal = () => {
        setIsKycModalOpen(false);
        // reset KYC modal state
        setAadhaarUploaded(false);
        setPanUploaded(false);
        setAadhaarFile(null);
        setPanFile(null);
        if (aadhaarPreviewUrl) {
            URL.revokeObjectURL(aadhaarPreviewUrl);
            setAadhaarPreviewUrl('');
        }
        if (panPreviewUrl) {
            URL.revokeObjectURL(panPreviewUrl);
            setPanPreviewUrl('');
        }
    };
    // const handleKycSubmit = () => {
    //     if (!aadhaarUploaded || !panUploaded) return;
    //     setKycVerified(true);
    //     setIsKycModalOpen(false);
    //     showSuccess('KYC Verified Successfully!');
    // };

    // UI badge should only reflect API response (not saved cache) and only after API has executed
    const isKycCompletedForBadge = kycLoaded && !!(kycData?.aadhaarVerified) && !!(kycData?.panVerified);

    // KYC file handlers
    const handleAadhaarSelect = (e) => {
        const file = e?.target?.files && e.target.files[0];
        if (!file) return;
        setAadhaarFile(file);
        setAadhaarUploaded(true);
        try {
            if (file.type && file.type.startsWith('image/')) {
                const url = URL.createObjectURL(file);
                setAadhaarPreviewUrl(url);
            } else {
                setAadhaarPreviewUrl('');
            }
        } catch (_) {
            setAadhaarPreviewUrl('');
        }
    };

    const handlePanSelect = (e) => {
        const file = e?.target?.files && e.target.files[0];
        if (!file) return;
        setPanFile(file);
        setPanUploaded(true);
        try {
            if (file.type && file.type.startsWith('image/')) {
                const url = URL.createObjectURL(file);
                setPanPreviewUrl(url);
            } else {
                setPanPreviewUrl('');
            }
        } catch (_) {
            setPanPreviewUrl('');
        }
    };

    const closeClaimModal = () => {
        setIsClaimModalOpen(false);
        document.body.style.overflow = '';
    };

    const handleSubmitClaim = async (e) => {
        e.preventDefault();
        if (claimSubmitting) return;

        const available = Number(earnings?.availableBalance || 0);
        const value = String(claimAmount || '').trim();
        // Allow only digits and optional decimal point
        const amountNum = Number(value.replace(/[^0-9.]/g, ''));
        if (!isFinite(amountNum)) {
            alert('Please enter a valid amount');
            return;
        }
        if (amountNum <= 0 || amountNum >= available) {
            alert(`Amount must be greater than ₹0 and less than your available balance ₹${available}`);
            return;
        }
        if (!upiId || !confirmUpiId || upiId.trim() !== confirmUpiId.trim()) {
            alert('UPI IDs do not match. Please check and try again.');
            return;
        }

        // Build inputs
        const mobileNumber = currentUser?.mobileNumber || apiService.getMobileNumber();
        const nameOnAadhaar = kycData?.nameOnAadhaar || currentUser?.name || currentUser?.fullName || 'User';
        if (!mobileNumber) {
            alert('Missing mobile number. Please sign in again.');
            return;
        }

        try {
            setClaimSubmitting(true);
            const resp = await apiService.directTransfer({
                mobileNumber,
                vpa: upiId.trim(),
                nameOnAadhar: nameOnAadhaar, // API expects nameOnAadhar (single a)
                transferAmount: String(amountNum)
            });

            if (resp && resp.success) {
                alert(`Transfer initiated successfully!\n\nAmount: ₹${amountNum}\nUPI ID: ${upiId}`);
                // Reset and close
                setClaimAmount('');
                setUpiId('');
                setConfirmUpiId('');
                closeClaimModal();
                // Refresh balance/transactions
                await refreshEarnings();
            } else {
                const msg = resp?.message || resp?.error || 'Transfer failed. Please try again.';
                alert(`Could not process transfer: ${msg}`);
            }
        } catch (err) {
            alert(`Unexpected error during transfer: ${err?.message || err}`);
        } finally {
            setClaimSubmitting(false);
        }
    };

    return (
        <>

            <div style={{ background: '#f8fafc' }}>
                {/* Blue Theme Earnings Header */}
                <section style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #2563eb 100%)', color: 'white', padding: windowWidth <= 480 ? '1.5rem 0' : windowWidth <= 768 ? '2rem 16px' : '2.5rem 24px', position: 'relative', overflow: 'hidden', boxSizing: 'border-box' }}>
                    <div style={{ position: 'relative', zIndex: 1, margin: windowWidth <= 480 ? '0 16px' : '0', boxSizing: 'border-box', width: windowWidth <= 480 ? 'calc(100% - 32px)' : '100%', maxWidth: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: windowWidth <= 768 ? 'flex-start' : 'center', flexDirection: windowWidth <= 768 ? 'column' : 'row', gap: windowWidth <= 768 ? '1.5rem' : '0' }}>
                            <div style={{ textAlign: 'left', width: '100%', maxWidth: '100%' }}>
                                <h1 style={{ fontSize: windowWidth <= 480 ? '0.9rem' : windowWidth <= 768 ? '1.75rem' : '2.25rem', marginBottom: '0.5rem', fontWeight: 700, lineHeight: 1.1, whiteSpace: 'nowrap', width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
                                    <i className="fas fa-wallet" style={{ color: 'rgba(255,255,255,0.9)', fontSize: windowWidth <= 480 ? '0.8rem' : windowWidth <= 768 ? '1.5rem' : '2.25rem', marginRight: windowWidth <= 480 ? '8px' : '0.5rem' }}></i>My Earnings
                                </h1>
                                <p style={{ fontSize: windowWidth <= 480 ? '0.8rem' : windowWidth <= 768 ? '0.875rem' : '1rem', opacity: 0.9, fontWeight: 400, margin: 0, lineHeight: '1.4' }}>
                                    Track your referral earnings and manage withdrawals
                                </p>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: windowWidth <= 480 ? '0.75rem' : '1rem', flexDirection: windowWidth <= 480 ? 'column' : 'row', width: windowWidth <= 768 ? '100%' : 'auto' }}>
                                <div style={{ display: isKycCompletedForBadge ? 'block' : 'none', color: 'white', fontSize: windowWidth <= 480 ? '0.75rem' : '0.875rem', fontWeight: 500 }}>
                                    <i className="fas fa-check-circle" style={{ color: '#10b981', marginRight: '0.5rem' }}></i>KYC Verified
                                </div>
                                <button onClick={handleClaimClick} id="claimBtn" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', color: 'white', padding: windowWidth <= 480 ? '0.75rem 1.5rem' : '1rem 2rem', border: '2px solid rgba(255,255,255,0.3)', borderRadius: windowWidth <= 480 ? '8px' : '12px', fontWeight: 600, cursor: 'pointer', fontSize: windowWidth <= 480 ? '0.875rem' : '1rem', transition: 'all 0.3s ease', display: 'flex', alignItems: 'center', gap: '0.5rem', width: windowWidth <= 480 ? '100%' : 'auto', justifyContent: 'center' }}>
                                    <i className="fas fa-download" style={{ fontSize: windowWidth <= 480 ? '0.875rem' : '1rem' }}></i> {windowWidth <= 480 ? 'Claim' : 'Claim Money'}
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Blue Theme Statistics */}
                <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', padding: window.innerWidth <= 480 ? '1rem 12px' : window.innerWidth <= 768 ? '1.5rem 16px' : '2rem 24px', borderBottom: '1px solid #e0e7ff' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 480 ? '1fr' : window.innerWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: window.innerWidth <= 480 ? '0.75rem' : window.innerWidth <= 768 ? '1rem' : '2rem', textAlign: 'center' }}>
                        <div style={{ background: 'white', padding: window.innerWidth <= 480 ? '1rem' : '1.5rem', borderRadius: window.innerWidth <= 480 ? '8px' : '12px', boxShadow: '0 2px 8px rgba(37, 99, 235, 0.1)', border: '1px solid #e0e7ff' }}>
                            <div style={{ fontSize: window.innerWidth <= 480 ? '1.5rem' : '2rem', fontWeight: 700, color: '#2563eb', marginBottom: '0.5rem' }}>
                                {loading ? '-' : `₹${earnings.totalEarnings.toLocaleString()}`}
                            </div>
                            <div style={{ fontSize: window.innerWidth <= 480 ? '0.75rem' : '0.875rem', color: '#64748b', fontWeight: 500 }}>{getText(COMMON_TEXT_KEYS.TOTAL_EARNINGS, 'Total Earnings')}</div>
                        </div>
                        <div style={{ background: 'white', padding: window.innerWidth <= 480 ? '1rem' : '1.5rem', borderRadius: window.innerWidth <= 480 ? '8px' : '12px', boxShadow: '0 2px 8px rgba(37, 99, 235, 0.1)', border: '1px solid #e0e7ff' }}>
                            <div style={{ fontSize: window.innerWidth <= 480 ? '1.5rem' : '2rem', fontWeight: 700, color: '#3b82f6', marginBottom: '0.5rem' }}>
                                {loading ? '-' : `₹${earnings.availableBalance.toLocaleString()}`}
                            </div>
                            <div style={{ fontSize: window.innerWidth <= 480 ? '0.75rem' : '0.875rem', color: '#64748b', fontWeight: 500 }}>{getText(COMMON_TEXT_KEYS.AVAILABLE_BALANCE, 'Available Balance')}</div>
                        </div>
                        <div style={{ background: 'white', padding: window.innerWidth <= 480 ? '1rem' : '1.5rem', borderRadius: window.innerWidth <= 480 ? '8px' : '12px', boxShadow: '0 2px 8px rgba(37, 99, 235, 0.1)', border: '1px solid #e0e7ff' }}>
                            <div style={{ fontSize: window.innerWidth <= 480 ? '1.5rem' : '2rem', fontWeight: 700, color: '#1d4ed8', marginBottom: '0.5rem' }}>
                                {loading ? '-' : `₹${earnings.totalWithdrawn.toLocaleString()}`}
                            </div>
                            <div style={{ fontSize: window.innerWidth <= 480 ? '0.75rem' : '0.875rem', color: '#64748b', fontWeight: 500 }}>{getText(COMMON_TEXT_KEYS.TOTAL_WITHDRAWN, 'Total Withdrawn')}</div>
                        </div>
                        <div style={{ background: 'white', padding: window.innerWidth <= 480 ? '1rem' : '1.5rem', borderRadius: window.innerWidth <= 480 ? '8px' : '12px', boxShadow: '0 2px 8px rgba(37, 99, 235, 0.1)', border: '1px solid #e0e7ff' }}>
                            <div style={{ fontSize: window.innerWidth <= 480 ? '1.5rem' : '2rem', fontWeight: 700, color: '#6366f1', marginBottom: '0.5rem' }}>
                                {loading ? '-' : `₹${earnings.pendingAmount.toLocaleString()}`}
                            </div>
                            <div style={{ fontSize: window.innerWidth <= 480 ? '0.75rem' : '0.875rem', color: '#64748b', fontWeight: 500 }}>{getText(COMMON_TEXT_KEYS.PENDING_AMOUNT, 'Pending Amount')}</div>
                        </div>
                    </div>
                </div>

                {/* KYC Verification Warning hidden on Earnings. */}

                {/* KYC Verification Section hidden on Earnings. Users are redirected to Profile > KYC. */}

                {/* Main Content */}
                <main style={{ background: 'white', padding: window.innerWidth <= 480 ? '12px' : window.innerWidth <= 768 ? '16px' : '24px' }}>
                    <div style={{ maxWidth: '100%', margin: '0 auto' }}>
                        <div style={{ background: 'white', borderRadius: window.innerWidth <= 480 ? '12px' : '16px', padding: window.innerWidth <= 480 ? '1rem' : '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb', width: '100%' }}>
                            <div style={{ marginBottom: window.innerWidth <= 480 ? '1rem' : '2rem', display: 'flex', justifyContent: 'space-between', alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center', flexDirection: window.innerWidth <= 768 ? 'column' : 'row', gap: window.innerWidth <= 768 ? '1rem' : '0' }}>
                                <div>
                                    <h2 style={{ color: '#1f2937', fontSize: window.innerWidth <= 480 ? '1.25rem' : window.innerWidth <= 768 ? '1.5rem' : '1.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: window.innerWidth <= 480 ? '0.5rem' : '0.75rem', marginBottom: '0.5rem' }}>
                                        <i className="fas fa-list" style={{ color: '#2563eb' }}></i> Transaction Ledger
                                    </h2>
                                    <p style={{ color: '#64748b', margin: 0 }}>Complete record of your earnings and withdrawals</p>
                                </div>
                                <button
                                    onClick={refreshEarnings}
                                    disabled={isRefreshing}
                                    style={{
                                        background: isRefreshing ? '#9ca3af' : '#2563eb',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '0.75rem 1rem',
                                        cursor: isRefreshing ? 'not-allowed' : 'pointer',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <i className={`fas ${isRefreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
                                    {isRefreshing ? 'Refreshing...' : 'Refresh'}
                                </button>
                            </div>
                            
                            <div className="ledger-table">
                                <div className="table-header" style={{ gridTemplateColumns: '1fr 1fr 2fr 1fr 1fr', padding: '1rem 2rem', background: '#f9fafb' }}>
                                    <div className="header-cell">Date</div>
                                    <div className="header-cell">Type</div>
                                    <div className="header-cell">Description</div>
                                    <div className="header-cell">Amount</div>
                                    <div className="header-cell">Status</div>
                                </div>
                                
                                <div className="table-body">
                                    {/* Loading State */}
                                    {loading && (
                                        <div style={{ padding: '3rem', textAlign: 'center' }}>
                                            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#2563eb', marginBottom: '1rem' }}></i>
                                            <p style={{ color: '#64748b', margin: 0 }}>Loading transactions...</p>
                                        </div>
                                    )}
                                    
                                    {/* Error State */}
                                    {error && (
                                        <div style={{ padding: '3rem', textAlign: 'center' }}>
                                            <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', color: '#ef4444', marginBottom: '1rem' }}></i>
                                            <h3 style={{ color: '#dc2626', margin: '0 0 0.5rem 0' }}>Error Loading Transactions</h3>
                                            <p style={{ color: '#7f1d1d', margin: '0 0 1rem 0' }}>{error}</p>
                                            <button
                                                onClick={refreshEarnings}
                                                style={{
                                                    background: '#ef4444',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    padding: '0.5rem 1rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                Try Again
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* Empty State */}
                                    {!loading && !error && transactions.length === 0 && (
                                        <div style={{ padding: '3rem', textAlign: 'center' }}>
                                            <i className="fas fa-receipt" style={{ fontSize: '2rem', color: '#6b7280', marginBottom: '1rem' }}></i>
                                            <h3 style={{ color: '#1f2937', margin: '0 0 0.5rem 0' }}>No Transactions Yet</h3>
                                            <p style={{ color: '#6b7280', margin: 0 }}>Your earnings and withdrawal history will appear here.</p>
                                        </div>
                                    )}
                                    
                                    {/* Transactions List */}
                                    {!loading && !error && transactions.length > 0 && transactions.map((t, index) => (
                                        <div key={t.id || index} className={`table-row ${t.isEarning ? 'earning' : 'withdrawal'}`} style={{ gridTemplateColumns: '1fr 1fr 2fr 1fr 1fr', padding: '1.5rem 2rem' }}>
                                            <div className="cell" data-label="Date">{t.date}</div>
                                            <div className="cell" data-label="Type">
                                                <div className={`transaction-type ${t.isEarning ? 'earning' : 'withdrawal'}`}>
                                                    <i className={`fas ${t.typeIcon}`}></i>
                                                    {t.type}
                                                </div>
                                            </div>
                                            <div className="cell" data-label="Description">
                                                <div className="transaction-description">
                                                    <strong>{t.description}</strong>
                                                    <span>{t.details}</span>
                                                </div>
                                            </div>
                                            <div className="cell" data-label="Amount">
                                                <div className={`amount ${t.isEarning ? 'earning' : 'withdrawal'}`} style={{ background: t.isEarning ? '#dcfce7' : '#fee2e2', color: t.isEarning ? '#16a34a' : '#dc2626', padding: '0.5rem 1rem', borderRadius: '8px', fontWeight: 700, textAlign: 'center' }}>
                                                    {t.isEarning ? '+' : ''}₹{Math.abs(t.amount).toLocaleString()}
                                                </div>
                                            </div>
                                            <div className="cell" data-label="Status">
                                                <div className="status completed">
                                                    <i className={`fas ${t.statusIcon}`}></i>
                                                    {t.status}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
            <Footer />

            {/* KYC Prompt Modal */}
            {isKycPromptOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1100
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            try { sessionStorage.setItem(KYC_PROMPT_DISMISSED_KEY, '1'); } catch (_) {}
                            setIsKycPromptOpen(false);
                        }
                    }}
                >
                    <div
                        style={{
                            background: 'white',
                            borderRadius: 16,
                            maxWidth: 460,
                            width: '90%',
                            padding: '1.5rem',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                            border: '1px solid #e5e7eb'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                            <div style={{ background: '#FEF3C7', color: '#D97706', width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: '#1f2937' }}>KYC Required</h3>
                        </div>
                        <p style={{ margin: '0 0 1rem 0', color: '#4b5563' }}>
                            To claim your earnings, please complete your KYC by uploading your Aadhaar and PAN.
                        </p>
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    try { sessionStorage.setItem(KYC_PROMPT_DISMISSED_KEY, '1'); } catch (_) {}
                                    setIsKycPromptOpen(false);
                                }}
                                style={{
                                    background: 'white',
                                    color: '#374151',
                                    border: '1px solid #d1d5db',
                                    borderRadius: 8,
                                    padding: '0.5rem 0.9rem',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    setIsKycPromptOpen(false);
                                    navigate('/profile', { state: { scrollTo: 'kyc' } });
                                }}
                                style={{
                                    background: '#2563eb',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 8,
                                    padding: '0.5rem 0.9rem',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                <i className="fas fa-id-card"></i>
                                Complete KYC
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* KYC Modal */}
            {isKycModalOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeKycModal();
                    }}
                >
                    <div
                        style={{
                            background: 'white',
                            borderRadius: 20,
                            maxWidth: 600,
                            width: '90%',
                            maxHeight: '90vh',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                            border: '1px solid #e5e7eb',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {/* Fixed Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #d97706, #f59e0b)',
                            color: 'white',
                            padding: '2rem',
                            borderRadius: '20px 20px 0 0',
                            position: 'relative',
                            flexShrink: 0
                        }}>
                            <h2 style={{
                                margin: 0,
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem'
                            }}>
                                <i className="fas fa-id-card"></i>
                                Complete KYC Verification
                            </h2>
                            <button 
                                onClick={closeKycModal} 
                                aria-label="Close"
                                style={{
                                    position: 'absolute',
                                    top: '1rem',
                                    right: '1rem',
                                    background: 'rgba(255,255,255,0.2)',
                                    border: 'none',
                                    color: 'white',
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
                                onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div style={{ padding: '2rem', overflowY: 'auto', flex: 1 }}>
                            <div style={{ marginBottom: '2rem' }}>
                                <h3 style={{
                                    margin: '0 0 1rem 0',
                                    color: '#1f2937',
                                    fontSize: '1.125rem',
                                    fontWeight: 600
                                }}>
                                    Required Documents
                                </h3>
                                
                                <div style={{ display: 'grid', gap: '1rem' }}>
                                    {/* Aadhaar Card Section */}
                                    <div style={{
                                        border: '2px solid #e5e7eb',
                                        borderRadius: 12,
                                        padding: '1.5rem',
                                        background: 'white'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '1rem',
                                            marginBottom: '1rem'
                                        }}>
                                            <i className="fas fa-id-card" style={{
                                                fontSize: '1.5rem',
                                                color: '#d97706'
                                            }}></i>
                                            <div style={{ flex: 1 }}>
                                                <h4 style={{
                                                    margin: '0 0 0.25rem 0',
                                                    color: '#1f2937',
                                                    fontWeight: 600
                                                }}>
                                                    Aadhaar Card
                                                </h4>
                                                <p style={{
                                                    margin: 0,
                                                    color: '#6b7280',
                                                    fontSize: '0.875rem'
                                                }}>
                                                    Upload clear photo of your Aadhaar card
                                                </p>
                                            </div>
                                            <div style={{
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                color: aadhaarUploaded ? '#10b981' : '#ef4444'
                                            }}>
                                                {aadhaarUploaded ? 'Uploaded' : 'Not Uploaded'}
                                            </div>
                                        </div>

                                        {/* File Preview Section */}
                                        {(aadhaarFile || aadhaarPreviewUrl) && (
                                            <div style={{
                                                display: 'block',
                                                marginBottom: '1rem',
                                                padding: '1rem',
                                                background: '#f8fafc',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 8
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '1rem',
                                                    marginBottom: aadhaarPreviewUrl ? '0.75rem' : 0
                                                }}>
                                                    <i className="fas fa-file-image" style={{
                                                        color: '#6b7280',
                                                        fontSize: '1.25rem'
                                                    }}></i>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: 500,
                                                            color: '#1f2937'
                                                        }}>
                                                            {aadhaarFile?.name || 'Selected file'}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '0.75rem',
                                                            color: '#6b7280'
                                                        }}>
                                                            Aadhaar Card Document
                                                        </div>
                                                    </div>
                                                </div>
                                                {aadhaarPreviewUrl && (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <img 
                                                            src={aadhaarPreviewUrl} 
                                                            alt="Aadhaar Preview" 
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: 200,
                                                                borderRadius: 8,
                                                                border: '1px solid #e5e7eb'
                                                            }} 
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Hidden input */}
                                        <input 
                                            ref={aadhaarInputRef} 
                                            type="file" 
                                            accept="image/*,.pdf" 
                                            style={{ display: 'none' }} 
                                            onChange={handleAadhaarSelect} 
                                        />

                                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                                            <button 
                                                onClick={() => aadhaarInputRef.current && aadhaarInputRef.current.click()} 
                                                type="button" 
                                                style={{
                                                    background: '#d97706',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '0.5rem 1rem',
                                                    borderRadius: 8,
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                    fontSize: '0.875rem',
                                                    transition: 'all 0.3s ease',
                                                    flex: 1
                                                }}
                                                onMouseOver={(e) => e.target.style.background = '#b45309'}
                                                onMouseOut={(e) => e.target.style.background = '#d97706'}
                                            >
                                                <i className="fas fa-upload" style={{ marginRight: '0.5rem' }}></i>
                                                Upload
                                            </button>
                                            <button 
                                                type="button" 
                                                disabled={!aadhaarUploaded} 
                                                style={{
                                                    background: aadhaarUploaded ? '#d97706' : '#d1d5db',
                                                    color: aadhaarUploaded ? 'white' : '#9ca3af',
                                                    border: 'none',
                                                    padding: '0.5rem 1rem',
                                                    borderRadius: 8,
                                                    fontWeight: 500,
                                                    cursor: aadhaarUploaded ? 'pointer' : 'not-allowed',
                                                    fontSize: '0.875rem',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                Submit for Verification
                                            </button>
                                        </div>
                                    </div>

                                    {/* PAN Card Section */}
                                    <div style={{
                                        border: '2px solid #e5e7eb',
                                        borderRadius: 12,
                                        padding: '1.5rem',
                                        background: 'white'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '1rem',
                                            marginBottom: '1rem'
                                        }}>
                                            <i className="fas fa-credit-card" style={{
                                                fontSize: '1.5rem',
                                                color: '#d97706'
                                            }}></i>
                                            <div style={{ flex: 1 }}>
                                                <h4 style={{
                                                    margin: '0 0 0.25rem 0',
                                                    color: '#1f2937',
                                                    fontWeight: 600
                                                }}>
                                                    PAN Card
                                                </h4>
                                                <p style={{
                                                    margin: 0,
                                                    color: '#6b7280',
                                                    fontSize: '0.875rem'
                                                }}>
                                                    Upload clear photo of your PAN card
                                                </p>
                                            </div>
                                            <div style={{
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                color: panUploaded ? '#10b981' : '#ef4444'
                                            }}>
                                                {panUploaded ? 'Uploaded' : 'Not Uploaded'}
                                            </div>
                                        </div>

                                        {/* File Preview Section */}
                                        {(panFile || panPreviewUrl) && (
                                            <div style={{
                                                display: 'block',
                                                marginBottom: '1rem',
                                                padding: '1rem',
                                                background: '#f8fafc',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 8
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '1rem',
                                                    marginBottom: panPreviewUrl ? '0.75rem' : 0
                                                }}>
                                                    <i className="fas fa-file-image" style={{
                                                        color: '#6b7280',
                                                        fontSize: '1.25rem'
                                                    }}></i>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: 500,
                                                            color: '#1f2937'
                                                        }}>
                                                            {panFile?.name || 'Selected file'}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '0.75rem',
                                                            color: '#6b7280'
                                                        }}>
                                                            PAN Card Document
                                                        </div>
                                                    </div>
                                                </div>
                                                {panPreviewUrl && (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <img 
                                                            src={panPreviewUrl} 
                                                            alt="PAN Preview" 
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: 200,
                                                                borderRadius: 8,
                                                                border: '1px solid #e5e7eb'
                                                            }} 
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Hidden input */}
                                        <input 
                                            ref={panInputRef} 
                                            type="file" 
                                            accept="image/*,.pdf" 
                                            style={{ display: 'none' }} 
                                            onChange={handlePanSelect} 
                                        />

                                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                                            <button 
                                                onClick={() => panInputRef.current && panInputRef.current.click()} 
                                                type="button" 
                                                style={{
                                                    background: '#d97706',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '0.5rem 1rem',
                                                    borderRadius: 8,
                                                    fontWeight: 500,
                                                    cursor: 'pointer',
                                                    fontSize: '0.875rem',
                                                    transition: 'all 0.3s ease',
                                                    flex: 1
                                                }}
                                                onMouseOver={(e) => e.target.style.background = '#b45309'}
                                                onMouseOut={(e) => e.target.style.background = '#d97706'}
                                            >
                                                <i className="fas fa-upload" style={{ marginRight: '0.5rem' }}></i>
                                                Upload
                                            </button>
                                            <button 
                                                type="button" 
                                                disabled={!panUploaded} 
                                                style={{
                                                    background: panUploaded ? '#d97706' : '#d1d5db',
                                                    color: panUploaded ? 'white' : '#9ca3af',
                                                    border: 'none',
                                                    padding: '0.5rem 1rem',
                                                    borderRadius: 8,
                                                    fontWeight: 500,
                                                    cursor: panUploaded ? 'pointer' : 'not-allowed',
                                                    fontSize: '0.875rem',
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                Submit for Verification
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Guidelines */}
                            <div style={{
                                background: '#f0f9ff',
                                padding: '1.5rem',
                                borderRadius: 12,
                                marginBottom: '2rem',
                                border: '1px solid #bae6fd'
                            }}>
                                <h4 style={{
                                    margin: '0 0 1rem 0',
                                    color: '#0c4a6e',
                                    fontSize: '1rem',
                                    fontWeight: 600
                                }}>
                                    <i className="fas fa-info-circle" style={{ marginRight: '0.5rem' }}></i>
                                    Important Guidelines
                                </h4>
                                <ul style={{
                                    margin: 0,
                                    paddingLeft: '1.5rem',
                                    color: '#374151',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.6
                                }}>
                                    <li>Ensure documents are clear and readable</li>
                                    <li>Photos should be well-lit with no shadows</li>
                                    <li>All four corners should be visible</li>
                                    <li>File size should not exceed 5MB</li>
                                    <li>Supported formats: JPG, PNG, PDF</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Claim Money Modal */}
            {isClaimModalOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) closeClaimModal();
                    }}
                >
                    <div style={{
                        background: 'white',
                        borderRadius: 20,
                        maxWidth: 500,
                        width: '90%',
                        maxHeight: '90vh',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                        border: '1px solid #e5e7eb',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {/* Header */}
                        <div style={{ background: 'linear-gradient(135deg, #2563eb, #3b82f6)', color: 'white', padding: '2rem', borderRadius: '20px 20px 0 0', position: 'relative', flexShrink: 0 }}>
                            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <i className="fas fa-download"></i> Claim Your Earnings
                            </h2>
                            <button onClick={closeClaimModal} aria-label="Close" style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: 40, height: 40, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s ease' }}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '2rem', overflowY: 'auto', flex: 1 }}>
                            {/* Available Balance */}
                            <div style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', padding: '1.5rem', borderRadius: 12, marginBottom: '2rem', textAlign: 'center' }}>
                                <h3 style={{ margin: 0, marginBottom: '.5rem', color: '#1f2937', fontSize: '1.125rem' }}>Available Balance</h3>
                                <p style={{ margin: 0, fontSize: '2.5rem', fontWeight: 800, color: '#2563eb' }}>₹{Number(earnings?.availableBalance || 0).toLocaleString()}</p>
                            </div>

                            <form onSubmit={handleSubmitClaim}>
                                {/* Amount */}
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', marginBottom: '.5rem', fontWeight: 600, color: '#374151', fontSize: '.875rem' }}>Claim Amount *</label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontWeight: 600 }}>₹</span>
                                        <input type="text" required placeholder="0"
                                            value={claimAmount} onChange={(e) => setClaimAmount(e.target.value)}
                                            style={{ width: '100%', padding: '1rem 1rem 1rem 2.5rem', border: '2px solid #f3f4f6', borderRadius: 12, fontSize: '1rem', color: '#374151', transition: 'all 0.3s ease', background: '#f9fafb' }}
                                            onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.backgroundColor = 'white'; }}
                                            onBlur={(e) => { e.currentTarget.style.borderColor = '#f3f4f6'; e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                                        />
                                    </div>
                                    <small style={{ color: '#6b7280', fontSize: '.75rem', marginTop: '.25rem', display: 'block' }}>Enter an amount greater than ₹0 and less than your available balance ₹{Number(earnings?.availableBalance || 0).toLocaleString()}</small>
                                </div>

                                {/* UPI ID */}
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', marginBottom: '.5rem', fontWeight: 600, color: '#374151', fontSize: '.875rem' }}>UPI ID *</label>
                                    <input type="text" required placeholder="yourname@paytm or yourname@googlepay"
                                        value={upiId} onChange={(e) => setUpiId(e.target.value)}
                                        style={{ width: '100%', padding: '1rem', border: '2px solid #f3f4f6', borderRadius: 12, fontSize: '1rem', color: '#374151', transition: 'all 0.3s ease', background: '#f9fafb' }}
                                        onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.backgroundColor = 'white'; }}
                                        onBlur={(e) => { e.currentTarget.style.borderColor = '#f3f4f6'; e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                                    />
                                    <small style={{ color: '#6b7280', fontSize: '.75rem', marginTop: '.25rem', display: 'block' }}>Enter your UPI ID for instant transfer</small>
                                </div>

                                {/* Confirm UPI ID */}
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', marginBottom: '.5rem', fontWeight: 600, color: '#374151', fontSize: '.875rem' }}>Confirm UPI ID *</label>
                                    <input type="text" required placeholder="Re-enter your UPI ID"
                                        value={confirmUpiId} onChange={(e) => setConfirmUpiId(e.target.value)}
                                        style={{ width: '100%', padding: '1rem', border: '2px solid #f3f4f6', borderRadius: 12, fontSize: '1rem', color: '#374151', transition: 'all 0.3s ease', background: '#f9fafb' }}
                                        onFocus={(e) => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.backgroundColor = 'white'; }}
                                        onBlur={(e) => { e.currentTarget.style.borderColor = '#f3f4f6'; e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                                    />
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                    <button type="button" onClick={closeClaimModal} style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '1rem 2rem', borderRadius: 12, fontWeight: 600, cursor: 'pointer', fontSize: '.875rem' }}>Cancel</button>
                                    <button type="submit" style={{ background: 'linear-gradient(135deg, #2563eb, #3b82f6)', color: 'white', border: 'none', padding: '1rem 2rem', borderRadius: 12, fontWeight: 600, cursor: 'pointer', fontSize: '.875rem', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                                        <i className="fas fa-paper-plane"></i> Submit Claim Request
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Earnings;


